import { Command } from "cmdk";
import { useLiveQuery } from "dexie-react-hooks";
import { Dialog as D } from "radix-ui";
import { VISION_MODES } from "@/color/cvd";
import { harmonizeAccents, keepLocked, randomPalette } from "@/color/generate";
import { PALETTE_INFO, PALETTE_KEYS } from "@/model/palette";
import { GROUPS, TOKENS } from "@/model/tokens";
import { db } from "@/presets/repo";
import { STARTERS } from "@/presets/starters";
import { useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import {
  backup,
  importFile,
  openPreset,
  openStarter,
  pickFile,
  pinCurrentAsB,
  save,
  saveAs,
  share,
  snapshot,
} from "../actions";
import { PREVIEW_TABS } from "../previews/stage";

function Item({
  children,
  onSelect,
  hint,
  value,
}: {
  children: React.ReactNode;
  onSelect: () => void;
  hint?: string;
  value?: string;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={() => {
        useUi.getState().setPalette(false);
        onSelect();
      }}
      className="flex cursor-default items-center gap-2 rounded-md px-2.5 py-1.5 text-sm data-[selected=true]:bg-hover"
    >
      <span className="flex-1">{children}</span>
      {hint && <span className="text-[11px] text-faint">{hint}</span>}
    </Command.Item>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:text-faint [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
    >
      {children}
    </Command.Group>
  );
}

export function CommandPalette() {
  const open = useUi((s) => s.palette);
  const setOpen = useUi((s) => s.setPalette);
  const presets = useLiveQuery(() => db.presets.toArray(), []);
  const e = useEditor.getState;
  const ui = useUi.getState;
  return (
    <D.Root open={open} onOpenChange={setOpen}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <D.Content className="fixed top-[14vh] left-1/2 z-50 w-[min(92vw,600px)] -translate-x-1/2 overflow-hidden rounded-xl border border-line bg-panel shadow-2xl">
          <D.Title className="sr-only">Command palette</D.Title>
          <D.Description className="sr-only">Search commands, presets and tokens</D.Description>
          <Command loop className="flex max-h-[60vh] flex-col">
            <Command.Input
              autoFocus
              placeholder="Type a command, preset or token…"
              className="h-12 border-line border-b bg-transparent px-4 text-sm outline-none placeholder:text-faint"
            />
            <Command.List className="overflow-y-auto p-1.5">
              <Command.Empty className="p-6 text-center text-muted text-sm">
                Nothing found.
              </Command.Empty>
              <Group heading="Actions">
                <Item onSelect={() => ui().openDialog("export")} hint="E">
                  Export / copy agent prompt
                </Item>
                <Item onSelect={save} hint="Ctrl S">
                  Save preset
                </Item>
                <Item onSelect={saveAs}>Save as copy</Item>
                <Item onSelect={() => snapshot()}>Take snapshot</Item>
                <Item onSelect={() => ui().openDialog("presets")} hint="Ctrl O">
                  Open presets
                </Item>
                <Item onSelect={share}>Copy share link</Item>
                <Item onSelect={pinCurrentAsB}>Pin current state as B</Item>
                <Item onSelect={() => e().toggleCompare()} hint="\">
                  Flip A / B
                </Item>
                <Item onSelect={() => ui().openTool("generate")}>Generate palette from a seed</Item>
                <Item onSelect={() => ui().openTool("adjust")}>
                  Adjust palette (hue, saturation, contrast)
                </Item>
                <Item
                  onSelect={() =>
                    e().edit((t) => ({ ...t, palette: harmonizeAccents(t.palette, t.locks) }))
                  }
                >
                  Harmonize accents
                </Item>
                <Item
                  onSelect={() =>
                    e().edit((t) => ({
                      ...t,
                      palette: keepLocked(t.palette, randomPalette(t.variant), t.locks),
                    }))
                  }
                  hint="Space"
                >
                  Randomize unlocked swatches
                </Item>
                <Item onSelect={() => e().set({ inspect: !e().inspect })} hint="I">
                  Toggle inspect mode
                </Item>
                <Item onSelect={() => e().set({ dogfood: !e().dogfood })}>Toggle dogfood mode</Item>
                <Item
                  onSelect={async () => {
                    const f = await pickFile();
                    if (f) await importFile(f);
                  }}
                >
                  Import theme file
                </Item>
                <Item onSelect={backup}>Back up all presets</Item>
              </Group>
              <Group heading="Preview">
                {PREVIEW_TABS.map((t, i) => (
                  <Item
                    key={t.id}
                    onSelect={() => e().set({ previewTab: t.id })}
                    hint={String(i + 1)}
                  >
                    Show {t.label}
                  </Item>
                ))}
                {VISION_MODES.map((m) => (
                  <Item key={m.id} onSelect={() => e().set({ vision: m.id })}>
                    Simulate {m.label.toLowerCase()}
                  </Item>
                ))}
              </Group>
              <Group heading="Presets">
                {(presets ?? []).map((p) => (
                  <Item key={p.id} value={`preset ${p.name}`} onSelect={() => openPreset(p)}>
                    Open {p.name}
                  </Item>
                ))}
                {STARTERS.map((s) => (
                  <Item
                    key={s.id}
                    value={`starter ${s.name}`}
                    onSelect={() => openStarter(s.id)}
                    hint="starter"
                  >
                    Start from {s.name}
                  </Item>
                ))}
              </Group>
              <Group heading="Palette">
                {PALETTE_KEYS.map((k) => (
                  <Item
                    key={k}
                    value={`swatch ${k} ${PALETTE_INFO[k].label}`}
                    onSelect={() => e().select({ kind: "palette", key: k })}
                    hint={`$${k}`}
                  >
                    {PALETTE_INFO[k].label}
                  </Item>
                ))}
              </Group>
              <Group heading="Tokens">
                {TOKENS.map((t) => (
                  <Item
                    key={t.id}
                    value={`token ${t.id} ${t.label}`}
                    onSelect={() => e().select({ kind: "token", id: t.id })}
                    hint={GROUPS.find((g) => g.id === t.group)?.label}
                  >
                    {t.label}
                  </Item>
                ))}
              </Group>
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
